// src/store/searchSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface BoatTypeOption {
  // Define the structure of BoatTypeOption here
  label: string;
  id: number;
}
interface YachtType {
  name: string;
  count: number;
}

interface Cabin {
  name: string;
  count: number;
}
interface YachtLocation {
  id: string;
  latitude: number;
  longitude: number;
}
interface LocalSearchData {
  city?: string;
  country?: string;
  checkIn?: Date | string;
  checkOut?: Date | string;
  selectedBoatType?: BoatTypeOption | null;
}

interface Counts {
  yacht_types: YachtType[];
  cabins: Cabin[];
  total_yachts: number;
  bathrooms: any[];
}
interface amenities {
  label: string;
}
interface SearchState {
  searchResults: any | null;
  boatTypes: BoatTypeOption[];
  selectedBoatType: BoatTypeOption | null;
  selectedLocation: any | null; // Define type if known
  checkIn: any;
  checkOut: any;
  selectedLanguage: string;
  counts: Counts;
  amenities: amenities[];
  yachtLocation: YachtLocation[];
  localSearchData: LocalSearchData | null;
  filterNext: string;
  filterPrev: string;
}

const initialState: SearchState = {
  amenities: [],
  searchResults: [],
  counts: {
    yacht_types: [],
    cabins: [],
    total_yachts: 0,
    bathrooms: [],
  },
  boatTypes: [],
  selectedBoatType: null,
  selectedLocation: null,
  checkIn: null,
  checkOut: null,
  selectedLanguage: "English",
  yachtLocation: [],
  localSearchData: null,
  filterNext: "",
  filterPrev: "",
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    setFilterNextAndPrev: (
      state,
      action: PayloadAction<{ filterPrev: string; filterNext: string }>
    ) => {
      state.filterPrev = action.payload.filterPrev;
      state.filterNext = action.payload.filterNext;
    },
    setLocalSearchData: (state, action: PayloadAction<LocalSearchData>) => {
      state.localSearchData = action.payload;
    },
    setSearchResultsSlice: (state, action: PayloadAction<any>) => {
      state.searchResults = action.payload;
    },
    setYachtLocation: (state, action: PayloadAction<YachtLocation[]>) => {
      state.yachtLocation = action.payload;
    },
    setBoatTypes: (state, action: PayloadAction<BoatTypeOption[]>) => {
      state.boatTypes = action.payload;
    },
    setSelectedBoatType: (
      state,
      action: PayloadAction<BoatTypeOption | null>
    ) => {
      state.selectedBoatType = action.payload;
    },
    setSelectedLocation: (state, action: PayloadAction<any | null>) => {
      state.selectedLocation = action.payload;
    },
    setAllData: (state, action: PayloadAction<any | null>) => {
      if (action.payload) {
        state.checkIn = action.payload.selectedCheckIn;
        state.checkOut = action.payload.selectedCheckOut;
        state.selectedBoatType = action.payload.selectedBoatType;
        state.selectedLocation = action.payload.selectedLocation;
        state.boatTypes = action.payload.boatTypes;
        // state.selectedBoatType = action.payload.selectedBoatType;
      }
    },

    setLanguage: (state, action: PayloadAction<any | null>) => {
      state.selectedLanguage = action.payload;
    },
    setCounts: (state, action: PayloadAction<any>) => {
      state.counts = action.payload;
    },
    mergeCounts: (state, action: PayloadAction<any>) => {
      if (!action.payload) return;
      const next = action.payload;
      const merge = (prev: { name: string; count: number }[], incoming: { name: string; count: number }[]) => {
        const map = new Map(prev.map(o => [o.name, o]));
        incoming.forEach(o => map.set(o.name, o));
        return Array.from(map.values());
      };
      state.counts = {
        total_yachts: next.total_yachts ?? 0,
        yacht_types: merge(state.counts.yacht_types ?? [], next.yacht_types ?? []),
        cabins: merge(state.counts.cabins ?? [], next.cabins ?? []),
        bathrooms: merge(state.counts.bathrooms ?? [], next.bathrooms ?? []),
      };
    },
    setAmenities: (state, action: PayloadAction<amenities[]>) => {
      state.amenities = action.payload;
    },
  },
});

export const {
  setFilterNextAndPrev,
  setSearchResultsSlice,
  setBoatTypes,
  setSelectedBoatType,
  setSelectedLocation,
  setAllData,
  setLanguage,
  setCounts,
  mergeCounts,
  setAmenities,
  setYachtLocation,
  setLocalSearchData,
} = searchSlice.actions;

export default searchSlice.reducer;
